import React, { createContext, useContext, useEffect, useState } from "react";
import i18n from "@/i18n";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
} from "firebase/auth";
import { auth, db } from "@/services/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import type { User } from "firebase/auth";

interface SessionData {
  completedProfile?: boolean;
  // add more flags later
}

interface SessionContextValue {
  user: User | null;
  session: SessionData | null;
  loading: boolean;
  firestoreBlocked: boolean;
  firestoreBlockedMessage?: string | null;
  storageBlocked: boolean;
}

const SessionContext = createContext<SessionContextValue>({
  user: null,
  session: null,
  loading: true,
  firestoreBlocked: false,
  firestoreBlockedMessage: null,
  storageBlocked: false,
});

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<SessionContextValue>({
    user: null,
    session: null,
    loading: true,
    firestoreBlocked: false,
    firestoreBlockedMessage: null,
    storageBlocked: false,
  });

  useEffect(() => {
    let mounted = true;
    let unsub: (() => void) | undefined;
    let authTimeout: NodeJS.Timeout | undefined;

    (async () => {
      let persistenceSet = false;
      let storageBlocked = false;

      // Set a timeout to prevent infinite loading in new tabs
      authTimeout = setTimeout(() => {
        if (mounted && state.loading) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      }, 4000); // Increased to 4 seconds for slower connections

      // Check if user is already available (for new tabs or after login)
      if (auth.currentUser) {
        clearTimeout(authTimeout);
        // Process the existing user immediately
        const firebaseUser = auth.currentUser;
        try {
          const sessionRef = doc(db, "sessions", firebaseUser.uid);
          const snap = await getDoc(sessionRef);
          if (!snap.exists()) {
            await setDoc(sessionRef, { createdAt: Date.now() });
          }

          // Update lastSeen only after the signup flow has created the user profile.
          const userRef = doc(db, "users", firebaseUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            await updateDoc(userRef, {
              lastSeen: new Date().toISOString(),
            });
          }

          if (mounted) {
            setState({
              user: firebaseUser,
              session: (snap.data() as SessionData) || {},
              loading: false,
              firestoreBlocked: false,
              firestoreBlockedMessage: null,
              storageBlocked: false,
            });
            return; // Skip the rest of the setup since we're already authenticated
          }
        } catch (err) {
          console.warn("Error processing existing user:", err);
        }
      }

      // Try to set initial persistence - this ensures proper session handling
      try {
        // First try local persistence (for "remember me" functionality)
        await setPersistence(auth, browserLocalPersistence);
        persistenceSet = true;
      } catch (e) {
        console.warn(
          "Local persistence failed, trying session persistence:",
          e
        );
        // Try session persistence as fallback
        try {
          await setPersistence(auth, browserSessionPersistence);
          persistenceSet = true;
          storageBlocked = true;
        } catch (e2) {
          console.error("Both persistence methods failed:", e2);
          // Both persistences failed
          persistenceSet = false;
          storageBlocked = true;
        }
      }
      if (!persistenceSet) {
        // Can't proceed with auth
        setState({
          user: null,
          session: null,
          loading: false,
          firestoreBlocked: false,
          firestoreBlockedMessage: null,
          storageBlocked,
        });
        return;
      }
      unsub = onAuthStateChanged(auth, async (firebaseUser) => {
        if (!mounted) return;

        // Clear timeout since auth state has been determined
        if (authTimeout) {
          clearTimeout(authTimeout);
          authTimeout = undefined;
        }

        if (firebaseUser) {
          try {
            const sessionRef = doc(db, "sessions", firebaseUser.uid);
            const snap = await getDoc(sessionRef);
            if (!snap.exists()) {
              try {
                await setDoc(sessionRef, { createdAt: Date.now() });
              } catch (err2) {
                if (!mounted) return;
                setState({
                  user: firebaseUser,
                  session: {},
                  loading: false,
                  firestoreBlocked: true,
                  firestoreBlockedMessage:
                    i18n.t("app.errors.generic"),
                  storageBlocked,
                });
                return;
              }
            }

            // Update lastSeen timestamp when user enters portal (including existing sessions)
            try {
              const userRef = doc(db, "users", firebaseUser.uid);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                await updateDoc(userRef, {
                  lastSeen: new Date().toISOString(),
                });
              }
            } catch (error) {
              console.warn("Failed to update lastSeen:", error);
              // Don't block the session if this fails
            }

            if (!mounted) return;
            setState({
              user: firebaseUser,
              session: (snap.data() as SessionData) || {},
              loading: false,
              firestoreBlocked: false,
              firestoreBlockedMessage: null,
              storageBlocked,
            });
          } catch (err) {
            if (!mounted) return;
            setState({
              user: firebaseUser,
              session: {},
              loading: false,
              firestoreBlocked: true,
              firestoreBlockedMessage:
                i18n.t("app.errors.generic"),
              storageBlocked,
            });
          }
        } else {
          setState({
            user: null,
            session: null,
            loading: false,
            firestoreBlocked: false,
            firestoreBlockedMessage: null,
            storageBlocked,
          });
        }
      });
    })();
    return () => {
      mounted = false;
      if (authTimeout) clearTimeout(authTimeout);
      if (unsub) unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
